import { useQuery } from '@tanstack/react-query'

import config from '@config'

import {
  SportCompetitionScheduleAPIResponse,
  SportCompetitionScheduleItem,
  SportCompetitionScheduleRound,
  UseSportEntitySchedule,
} from './types'

const {
  sports: {
    baseUrlV2: sportsBaseUrlV2,
    sportEntityScheduleCompetitionUrlTemplateV2,
  },
} = config

const fetchSportCompetitionScheduleData = async ({
  typeOfSport,
  entityId,
}: UseSportEntitySchedule) => {
  const requestUrl = `${sportsBaseUrlV2}${sportEntityScheduleCompetitionUrlTemplateV2
    .replace('{SPORT}', typeOfSport)
    .replace('{API_VERSION}', 'v0')
    .replace('{SEASON_ID}', entityId)}`

  const response = await fetch(requestUrl)

  if (!response.ok) {
    throw new Error('Error fetching League Upcoming Games.')
  }

  const responseData = await response.json()

  if (!Array.isArray(responseData?.items)) {
    throw new Error('Invalid response from server')
  }

  const { rounds, items } =
    responseData as unknown as Awaited<SportCompetitionScheduleAPIResponse>

  const initialAccumulator = Object.fromEntries(
    rounds.map((round) => [round.id, []]),
  ) as Record<
    SportCompetitionScheduleRound['id'],
    SportCompetitionScheduleItem[]
  >

  const finalResponse = items.reduce((acc, item) => {
    acc[item.round.id].push(item)
    return acc
  }, initialAccumulator)

  return { rounds: rounds, items: finalResponse }
}

const useSportCompetitionSchedule = ({
  typeOfSport,
  entityId,
}: UseSportEntitySchedule) =>
  useQuery({
    queryKey: ['sportCompetitionSchedule', typeOfSport, entityId],
    queryFn: () =>
      fetchSportCompetitionScheduleData({
        typeOfSport,
        entityId,
      }),
  })

export default useSportCompetitionSchedule
